import React, { useEffect, useMemo, useState } from "react";
import { Syringe, Package, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { PatientLanguageSelect } from "./PatientLanguageSelect";
import { hasStaticEducation, patientLanguageName } from "../lib/patientLanguages";

/**
 * Auto-detects GLP-1 agonists in the current Rx and offers a one-click
 * "Prepare Companion Kit" button that generates the branded Do's/Don'ts +
 * Protein Guide handout and opens the printable page in a new tab.
 */
const GLP1_KEYWORDS = [
  "semaglutide", "ozempic", "rybelsus", "wegovy",
  "liraglutide", "victoza", "saxenda",
  "dulaglutide", "trulicity",
  "tirzepatide", "mounjaro", "zepbound",
  "exenatide", "byetta", "bydureon",
  "lixisenatide", "adlyxin",
];

function isGLP1(m) {
  const blob = [m.brand_name, m.generic_name, m.generic, m.composition].filter(Boolean).join(" ").toLowerCase();
  return GLP1_KEYWORDS.some((k) => blob.includes(k));
}

export default function GLP1CompanionKitPanel({ patientId, medicines, defaultLang = "mr", lockedLanguage = false }) {
  const [lang, setLang] = useState(defaultLang);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLang(defaultLang); }, [defaultLang, patientId]);
  const available = hasStaticEducation(lang);

  const detected = useMemo(() => (medicines || []).filter(isGLP1), [medicines]);
  if (detected.length === 0) return null;

  const prepareKit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/glp1/companion-kit", {
        patient_id: patientId,
        language: lang,
        medicines: detected.map((m) => ({
          brand_name: m.brand_name,
          generic_name: m.generic_name || m.generic,
          composition: m.composition,
        })),
      });
      const kitId = r.data.kit.kit_id;
      toast.success(lang === "mr" ? "किट तयार आहे" : "Kit prepared", { description: kitId });
      window.open(`/glp1/kit/${kitId}`, "_blank");
    } catch (e) {
      console.warn("kit generate failed:", e);
      toast.error("Could not prepare GLP-1 companion kit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-pink-300 bg-gradient-to-r from-pink-50 via-white to-pink-50 p-3 mb-3" data-testid="glp1-companion-panel">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mt-0.5 rounded-full bg-pink-100 p-2">
          <Syringe size={18} className="text-pink-700" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-sm text-slate-900">GLP-1 detected — Diabesity Companion Kit</div>
            <div className="text-[10px] px-1.5 py-0.5 rounded bg-pink-600 text-white font-medium uppercase tracking-wider">Auto</div>
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            {detected.map((m) => m.brand_name || m.generic_name || m.generic).filter(Boolean).join(", ")}
            {" — "}bundle the printable Do's/Don'ts sheet + Protein Guide with this Rx.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PatientLanguageSelect value={lang} onChange={setLang} disabled={lockedLanguage || busy} testId="glp1-kit-language" />
          {!available && <p data-testid="glp1-kit-unavailable" className="text-xs text-amber-800">Kit unavailable in {patientLanguageName(lang)}. Use Education Hub.</p>}
          <Button size="sm" onClick={prepareKit} disabled={busy || !available} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="glp1-kit-prepare-btn">
            <Package size={14} className="mr-1" />
            {busy ? (lang === "mr" ? "तयार करत आहे…" : "Preparing…") : (lang === "mr" ? "किट तयार करा" : "Prepare Kit")}
            {!busy && <ExternalLink size={11} className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
